import { useRef, useEffect } from "react";
import pfp from "./pfp";
import { Spinner } from "react-bootstrap";
import ReplyMessage from "./ReplyMessage";
import { formatBytes, truncateFileName } from "../../../helpers";
import config from "../../../config";
import documentPreviewIcon from "../../../assets/document_preview.svg";
import { useNavigate } from "react-router-dom";
import "./MessageContainer.css";

const Messages = ({
  loading,
  channelMessages,
  userInfo,
  props,

  //Custom fields
  showDate = true,
  messageRecipientClassname = "message-recipient",
  messageAuthorClassname = "message-author",
  overflowY = "scroll",

  // If can send messages
  chatBarHeight,
  replyToPadding,

  // If images/downloading supported
  handleDownload = () => {},

  // if can't interact, don't set these
  chatRef,
  setFlipMessageOptions = () => {},
  flipMessageOptions = null,
  hoverOnMessage = null,
  setHoverOnMessage = () => {},
  messageDropdownOpen = null,
  setMessageDropdownOpen = () => {},
  handleMessageEditClick = () => {},
  handleMessageDeleteClick = () => {},
  handleMessageReplyClick = () => {},
  setCurrentTarget = () => {},
}) => {
  const divRef = useRef(null);
  const navigate = useNavigate();
  const validPreviewExtensions = config.VALID_PREVIEW_EXTENSIONS;

  // Return 1 if chat partner, -1 if user, 0 otherwise
  const calcMessageClumpPfp = (sender, index) => {
    if (sender) {
      if (
        channelMessages[index + 1]?.uid !== (userInfo?.uid ?? "") ||
        index === channelMessages.length - 1
      ) {
        return -1;
      }
    }
    if (!sender) {
      if (
        channelMessages[index + 1]?.uid === (userInfo?.uid ?? "") ||
        index === channelMessages.length - 1
      ) {
        return 1;
      }
    }
    return 0;
  };

  const calcMessageClumpTimestamp = (sender, index) => {
    if (sender) {
      if (
        channelMessages[index - 1]?.uid !== (userInfo?.uid ?? "") ||
        index === 0
      ) {
        return -1;
      }
    }
    if (!sender) {
      if (
        channelMessages[index - 1]?.uid === (userInfo?.uid ?? "") ||
        index === 0
      ) {
        return 1;
      }
    }
    return 0;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
    const day = date.toLocaleDateString("en-US", { day: "numeric" });
    const month = date.toLocaleDateString("en-US", { month: "short" });
    const time = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return `${weekday} ${day} ${month}, ${time}`;
  };

  const getLoomEmbed = (message) => {
    const words = message.split(/\s+/);
    const loomWord = words.filter((word) =>
      word.startsWith("https://www.loom.com/share/"),
    )[0];
    return loomWord
      ? loomWord.replace(
          "https://www.loom.com/share/",
          "https://www.loom.com/embed/",
        )
      : "";
  };

  const isOnlyLoom = (message) => {
    return (
      message.body.message.includes("https://www.loom.com/share/") &&
      message.body.message.split(/\s+/).length === 1 &&
      message.body.files.length === 0
    );
  };

  const hasLoomURL = (message) => {
    return message.includes("https://www.loom.com/share/");
  };

  const invisibleAnchorRef = useRef(null);

  useEffect(() => {
    invisibleAnchorRef.current.scrollIntoView({ behavior: "auto" });
  }, [channelMessages]);

  return (
    <div
      ref={chatRef}
      style={{
        marginTop: "5px",
        maxHeight: `calc(100vh - ${
          230 + chatBarHeight + replyToPadding + 80
        }px)`,
        minHeight: `calc(100vh - ${
          230 + chatBarHeight + replyToPadding + 80
        }px)`,
        overflowY: overflowY,
        overflowX: "hidden",
        marginLeft: "-100px",
        paddingLeft: "100px",
        paddingRight: "66px",
        marginRight: "-80px",
        paddingBottom: "50px",
        transition: "0.3s",
      }}
    >
      <div
        ref={divRef}
        style={{
          maxWidth: "1020px",
          width: "100%",
          display: "flex",
          flexDirection: "column-reverse",
          height: "100%",
        }}
      >
        <div ref={invisibleAnchorRef} id="invisible-anchor"></div>

        {loading ? (
          <div
            style={{
              height: "100%",
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Spinner
              style={{
                height: "70px",
                width: "70px",
                marginTop: "20%",
              }}
            />
          </div>
        ) : (
          channelMessages.map((message, i) => {
            const sender = message.uid === (userInfo?.uid ?? "");
            const showPfp = calcMessageClumpPfp(sender, i);
            const showTimestamp = calcMessageClumpTimestamp(sender, i);
            const loom = hasLoomURL(message.body.message);
            const onlyLoom = loom && isOnlyLoom(message);
            const replyOf =
              channelMessages.find((m) => m.id === message.replyingTo) ?? null;

            const hasActions =
              message.takeActions && message.takeActions.length > 0;

            return (
              <div key={i} id={message.id}>
                {!onlyLoom && (
                  <div
                    className={`justify-content-${
                      sender ? "start" : "end"
                    } message-container d-flex`}
                    style={{ overflow: "visible" }}
                    key={message.id}
                  >
                    {showPfp === -1 && pfp(userInfo?.pfp, 20, true)}
                    <div>
                      <div
                        onClick={() => {
                          setHoverOnMessage(message.id);
                        }}
                        onMouseEnter={() => {
                          if (!hoverOnMessage) {
                            setHoverOnMessage(message.id);
                          }
                        }}
                        onMouseLeave={() => {
                          if (!messageDropdownOpen) {
                            setHoverOnMessage(null);
                          }
                        }}
                        className={`message ${
                          sender
                            ? messageRecipientClassname
                            : messageAuthorClassname
                        }`}
                        style={{
                          marginLeft: showPfp === -1 ? "0px" : "60px",
                          marginRight: showPfp === 1 ? "0px" : "60px",
                        }}
                      >
                        {hoverOnMessage === message.id && (
                          <div
                            style={{ position: "relative" }}
                            onClick={(event) => {
                              setMessageDropdownOpen(message.id);
                              setCurrentTarget(event.currentTarget);
                              const { top: clickedTop } =
                                event.currentTarget.getBoundingClientRect();
                              const { top: chatTop, bottom: chatBottom } =
                                chatRef.current.getBoundingClientRect();
                              const relativeTop = clickedTop - chatTop;

                              setFlipMessageOptions(
                                relativeTop >= 200 &&
                                  chatBottom - relativeTop - 350 < 0,
                              );
                            }}
                          >
                            <div className="hover-box">
                              <div className="dots">
                                <span></span>
                                <span></span>
                                <span></span>
                              </div>
                            </div>
                          </div>
                        )}
                        {messageDropdownOpen === message.id && (
                          <div
                            style={{
                              position: "relative",
                            }}
                          >
                            <div
                              style={{
                                top:
                                  flipMessageOptions && sender
                                    ? "-172px"
                                    : "32px",
                              }}
                              className={`${
                                sender
                                  ? "message-options"
                                  : "message-options-flip"
                              } fs-16 fc-black lh-216 fw-500`}
                            >
                              {sender && (
                                <>
                                  <div
                                    onClick={() =>
                                      handleMessageEditClick(message)
                                    }
                                    className="message-options-item"
                                  >
                                    <i className="fa-regular fa-pen fs-20"></i>
                                    <span style={{ paddingLeft: "13px" }}>
                                      Edit
                                    </span>
                                  </div>
                                  <div
                                    onClick={() =>
                                      handleMessageDeleteClick(message.id)
                                    }
                                    className="message-options-item"
                                  >
                                    <i className="fa-regular fa-trash fs-20"></i>
                                    <span style={{ paddingLeft: "15px" }}>
                                      Delete
                                    </span>
                                  </div>
                                </>
                              )}

                              <div
                                onClick={() => {
                                  handleMessageReplyClick(message);
                                }}
                                className="message-options-item"
                              >
                                <i className="fa-regular fa-reply fs-20"></i>
                                <span style={{ paddingLeft: "12px" }}>
                                  Reply
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        <ReplyMessage
                          replyingTo={replyOf}
                          userInfo={userInfo}
                        />
                        {message?.body?.files?.length !== 0 && (
                          <div>
                            {message?.body?.files?.map((file, idx) => {
                              const filenameWithoutParams = file.url.replace(
                                /\?.*$/,
                                "",
                              );
                              return (
                                <div
                                  key={idx}
                                  className={`send-chatbar-media-container ${
                                    sender ? "media-recipient" : "media-author"
                                  }`}
                                  onClick={handleDownload(file)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <div className="preview-div">
                                    <img
                                      alt={"file preview"}
                                      src={
                                        validPreviewExtensions.some(
                                          (extension) =>
                                            filenameWithoutParams.endsWith(
                                              extension,
                                            ),
                                        )
                                          ? file.url
                                          : documentPreviewIcon
                                      }
                                    />
                                  </div>
                                  <div
                                    className={`no-url${
                                      sender ? "-recipient" : "-author"
                                    }`}
                                  >
                                    <div className="information-div">
                                      {truncateFileName(
                                        decodeURI(
                                          filenameWithoutParams.replace(
                                            /^.*(%2F|\/)/, // Removes any firestore encoded characters from the filename
                                            "",
                                          ),
                                        ),
                                      )}

                                      <h3>{formatBytes(file.size)}</h3>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {Array.isArray(message.body.message) ? (
                          message.body.message.map((item, index) => (
                            <div key={index}>{item}</div>
                          ))
                        ) : (
                          <div style={{ whiteSpace: "pre-wrap" }}>
                            {message.body.message}
                          </div>
                        )}

                        {hasActions && (
                          <div>
                            <div className="mt-25 mb-20 fs-16 fw-700 fc-black">
                              Take actions
                            </div>
                            {message.takeActions.map((action, index) => (
                              <div
                                key={index}
                                className="take-action-item fc-black fs-16 fw-500"
                                onClick={() => navigate(action.value)}
                              >
                                <div className="take-action-item-content">
                                  <div className="">
                                    {config.TAKE_ACTIONS_ICONS[
                                      action.label
                                    ] && (
                                      <i
                                        className={`mr-10 fa-regular ${
                                          config.TAKE_ACTIONS_ICONS[
                                            action.label
                                          ]
                                        }`}
                                      ></i>
                                    )}
                                    {action.label}
                                  </div>
                                  <div>
                                    <i class="fvc fs-20 fa-light fa-arrow-right-long take-action-icon"></i>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {showPfp === 1 &&
                      pfp(
                        props?.openedChannel?.icon,
                        -20,
                        props?.chatPartnerStatus === "online",
                      )}
                  </div>
                )}

                <div>
                  {loom && (
                    <div
                      className={`justify-content-${
                        sender ? "start" : "end"
                      } message-container d-flex`}
                      style={{ overflow: "visible" }}
                      key={message.id}
                    >
                      {showPfp === -1 && pfp(userInfo?.pfp, 20, true)}
                      <div
                        style={{
                          width: "270px",
                          height: "180px",
                          marginLeft: !sender
                            ? "0px"
                            : showPfp === -1
                              ? "0px"
                              : "60px",
                          marginRight: sender
                            ? "0px"
                            : showPfp === 1
                              ? "0px"
                              : "60px",
                          marginTop: "12px",
                        }}
                      >
                        <iframe
                          style={{ width: "270px", height: "180px" }}
                          className={`justify-content-${
                            sender ? "start" : "end"
                          } rounded-iframe`}
                          src={getLoomEmbed(message.body.message)}
                          title="YouTube Video"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    </div>
                  )}
                </div>
                <div className={showDate ? "" : "mb-8"}>
                  {showDate &&
                    (showTimestamp === 1 || showTimestamp === -1) && (
                      <div
                        style={{
                          textAlign: sender ? "start" : "end",
                          paddingLeft: !sender ? "0px" : "60px",
                          marginRight: sender ? "0px" : "60px",
                          marginTop: "12px",
                        }}
                        className="fw-500 fs-14 lh-189 fc-grey"
                      >
                        {formatTimestamp(message.created || message.createdAt)}
                      </div>
                    )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Messages;
